import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Search, Menu, X, Copy, Check, ChevronRight, ChevronDown, Sun, Moon,
  Link2, BookOpen, Key, Database, Webhook, Box, AlertTriangle, Package,
  History, ExternalLink, Zap, Shield, Code2, Terminal, Github, Info,
  AlertCircle, Lightbulb,
} from "lucide-react";

// =============================================================================
// CONSTANTS
// =============================================================================

const BASE_URL = "https://api.legacygrowthengine.com/v1";
const API_VERSION = "2026-01-15";
const RATE_LIMIT = { used: 847, total: 10000, reset: "in 42 minutes" };

type Lang = "javascript" | "python" | "curl";

const LANGS: { id: Lang; label: string }[] = [
  { id: "javascript", label: "JavaScript" },
  { id: "python", label: "Python" },
  { id: "curl", label: "cURL" },
];

// =============================================================================
// SIDEBAR STRUCTURE
// =============================================================================

const NAV_GROUPS: { id: string; title: string; icon: any; items: { id: string; title: string }[] }[] = [
  {
    id: "getting-started", title: "Getting Started", icon: BookOpen,
    items: [
      { id: "introduction", title: "Introduction" },
      { id: "quickstart", title: "Quickstart" },
      { id: "base-url", title: "Base URL" },
      { id: "api-versioning", title: "API Versioning" },
    ],
  },
  {
    id: "authentication", title: "Authentication", icon: Key,
    items: [
      { id: "api-keys", title: "API Keys" },
      { id: "bearer-tokens", title: "Bearer Tokens" },
      { id: "scopes", title: "Scopes" },
      { id: "key-rotation", title: "Key Rotation" },
    ],
  },
  {
    id: "leads", title: "Leads", icon: Database,
    items: [
      { id: "leads-list", title: "List leads" },
      { id: "leads-create", title: "Create a lead" },
      { id: "leads-retrieve", title: "Retrieve a lead" },
      { id: "leads-update", title: "Update a lead" },
      { id: "leads-delete", title: "Delete a lead" },
      { id: "leads-assign", title: "Assign a lead" },
      { id: "leads-score", title: "Re-score a lead" },
    ],
  },
  {
    id: "referral-contacts", title: "Referral Contacts", icon: Database,
    items: [
      { id: "contacts-list", title: "List contacts" },
      { id: "contacts-create", title: "Create a contact" },
      { id: "contacts-update", title: "Update a contact" },
      { id: "contacts-delete", title: "Delete a contact" },
    ],
  },
  {
    id: "organizations", title: "Organizations", icon: Database,
    items: [
      { id: "orgs-retrieve", title: "Retrieve organization" },
      { id: "orgs-update", title: "Update organization" },
    ],
  },
  {
    id: "team", title: "Team Members", icon: Database,
    items: [
      { id: "team-list", title: "List team members" },
      { id: "team-invite", title: "Invite a member" },
      { id: "team-retrieve", title: "Retrieve a member" },
      { id: "team-update", title: "Update a member" },
      { id: "team-remove", title: "Remove a member" },
    ],
  },
  {
    id: "analytics", title: "Analytics", icon: Database,
    items: [
      { id: "analytics-overview", title: "Overview metrics" },
      { id: "analytics-leads", title: "Lead analytics" },
      { id: "analytics-conversion", title: "Conversion funnel" },
      { id: "analytics-team", title: "Team activity" },
    ],
  },
  {
    id: "outreach", title: "Email Outreach", icon: Database,
    items: [
      { id: "outreach-generate", title: "Generate email" },
      { id: "outreach-send", title: "Send email" },
      { id: "outreach-schedule", title: "Schedule email" },
    ],
  },
  {
    id: "webhooks", title: "Webhooks", icon: Webhook,
    items: [
      { id: "webhooks-overview", title: "Overview" },
      { id: "webhooks-events", title: "Event types" },
      { id: "webhooks-signatures", title: "Verifying signatures" },
      { id: "webhooks-retry", title: "Retry policy" },
      { id: "webhooks-testing", title: "Testing webhooks" },
    ],
  },
  {
    id: "data-models", title: "Data Models", icon: Box,
    items: [
      { id: "model-lead", title: "Lead object" },
      { id: "model-contact", title: "Contact object" },
      { id: "model-organization", title: "Organization object" },
      { id: "model-team-member", title: "Team Member object" },
      { id: "model-event", title: "Event object" },
      { id: "model-error", title: "Error object" },
    ],
  },
  {
    id: "errors", title: "Errors", icon: AlertTriangle,
    items: [
      { id: "errors-codes", title: "Error codes" },
      { id: "errors-handling", title: "Handling errors" },
      { id: "errors-rate-limit", title: "Rate limiting" },
    ],
  },
  {
    id: "sdks", title: "SDKs & Tools", icon: Package,
    items: [
      { id: "sdk-node", title: "Node.js SDK" },
      { id: "sdk-python", title: "Python SDK" },
      { id: "sdk-cli", title: "Command-line tool" },
      { id: "sdk-postman", title: "Postman collection" },
      { id: "sdk-openapi", title: "OpenAPI spec" },
    ],
  },
  {
    id: "changelog", title: "Changelog", icon: History,
    items: [
      { id: "changelog-v1", title: "v1.0.0 — Initial release" },
    ],
  },
];

// =============================================================================
// SEARCH INDEX
// =============================================================================

type SearchEntry = {
  id: string; title: string; breadcrumb: string; keywords: string;
  method?: string; path?: string;
};

const SEARCH_INDEX: SearchEntry[] = NAV_GROUPS.flatMap((group) =>
  group.items.map((item) => ({
    id: item.id,
    title: item.title,
    breadcrumb: group.title,
    keywords: `${item.title} ${group.title}`.toLowerCase(),
  }))
);

// =============================================================================
// CODE EXAMPLES
// =============================================================================

type CodeSnippets = Record<Lang, string>;

const CODE: Record<string, CodeSnippets> = {
  quickstart: {
    javascript: `import LegacyGrowth from '@legacygrowth/node';

const lge = new LegacyGrowth(process.env.LGE_API_KEY);

const lead = await lge.leads.create({
  name: 'Margaret Thompson',
  zip_code: '22102',
  care_needs: 'Live-in dementia care',
  hours_needed: 168,
  payer_source: 'private_pay',
  urgency: 4,
});

console.log(lead.id, lead.score);`,
    python: `import legacygrowth

lge = legacygrowth.Client(api_key="LGE_API_KEY")

lead = lge.leads.create(
    name="Margaret Thompson",
    zip_code="22102",
    care_needs="Live-in dementia care",
    hours_needed=168,
    payer_source="private_pay",
    urgency=4,
)

print(lead.id, lead.score)`,
    curl: `curl https://api.legacygrowthengine.com/v1/leads \\
  -H "Authorization: Bearer $LGE_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Margaret Thompson",
    "zip_code": "22102",
    "care_needs": "Live-in dementia care",
    "hours_needed": 168,
    "payer_source": "private_pay",
    "urgency": 4
  }'`,
  },
  "leads-list": {
    javascript: `const leads = await lge.leads.list({
  limit: 50,
  status: 'new',
  min_score: 70,
});`,
    python: `leads = lge.leads.list(limit=50, status="new", min_score=70)`,
    curl: `curl "https://api.legacygrowthengine.com/v1/leads?limit=50&status=new&min_score=70" \\
  -H "Authorization: Bearer $LGE_API_KEY"`,
  },
  "leads-create": {
    javascript: `const lead = await lge.leads.create({
  name: 'Robert Chen',
  zip_code: '22030',
  care_needs: 'Post-surgical recovery, 4 hours/day',
  hours_needed: 28,
  payer_source: 'long_term_care_insurance',
  urgency: 3,
});`,
    python: `lead = lge.leads.create(
    name="Robert Chen",
    zip_code="22030",
    care_needs="Post-surgical recovery",
    hours_needed=28,
    payer_source="long_term_care_insurance",
    urgency=3,
)`,
    curl: `curl https://api.legacygrowthengine.com/v1/leads \\
  -H "Authorization: Bearer $LGE_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"name":"Robert Chen","zip_code":"22030","hours_needed":28,"urgency":3}'`,
  },
  "leads-retrieve": {
    javascript: `const lead = await lge.leads.retrieve('lead_8f3a2c1b');`,
    python: `lead = lge.leads.retrieve("lead_8f3a2c1b")`,
    curl: `curl https://api.legacygrowthengine.com/v1/leads/lead_8f3a2c1b \\
  -H "Authorization: Bearer $LGE_API_KEY"`,
  },
  "leads-update": {
    javascript: `const lead = await lge.leads.update('lead_8f3a2c1b', {
  status: 'contacted',
  notes: 'Left voicemail at 2:14pm. Family will call back tomorrow.',
});`,
    python: `lead = lge.leads.update("lead_8f3a2c1b", status="contacted")`,
    curl: `curl -X PATCH https://api.legacygrowthengine.com/v1/leads/lead_8f3a2c1b \\
  -H "Authorization: Bearer $LGE_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"status":"contacted"}'`,
  },
  "leads-delete": {
    javascript: `await lge.leads.delete('lead_8f3a2c1b');`,
    python: `lge.leads.delete("lead_8f3a2c1b")`,
    curl: `curl -X DELETE https://api.legacygrowthengine.com/v1/leads/lead_8f3a2c1b \\
  -H "Authorization: Bearer $LGE_API_KEY"`,
  },
  "leads-assign": {
    javascript: `await lge.leads.assign('lead_8f3a2c1b', { user_id: 'usr_7c2e4d1a' });`,
    python: `lge.leads.assign("lead_8f3a2c1b", user_id="usr_7c2e4d1a")`,
    curl: `curl -X POST https://api.legacygrowthengine.com/v1/leads/lead_8f3a2c1b/assign \\
  -H "Authorization: Bearer $LGE_API_KEY" \\
  -d '{"user_id":"usr_7c2e4d1a"}'`,
  },
  "leads-score": {
    javascript: `const lead = await lge.leads.rescore('lead_8f3a2c1b');`,
    python: `lead = lge.leads.rescore("lead_8f3a2c1b")`,
    curl: `curl -X POST https://api.legacygrowthengine.com/v1/leads/lead_8f3a2c1b/rescore \\
  -H "Authorization: Bearer $LGE_API_KEY"`,
  },
  "contacts-list": {
    javascript: `const contacts = await lge.contacts.list({ source_type: 'hospital' });`,
    python: `contacts = lge.contacts.list(source_type="hospital")`,
    curl: `curl "https://api.legacygrowthengine.com/v1/contacts?source_type=hospital" \\
  -H "Authorization: Bearer $LGE_API_KEY"`,
  },
  "contacts-create": {
    javascript: `const contact = await lge.contacts.create({
  org_name: 'Inova Fairfax Hospital',
  contact_name: 'Sarah Mitchell, RN',
  source_type: 'hospital',
  zip_code: '22042',
});`,
    python: `contact = lge.contacts.create(
    org_name="Inova Fairfax Hospital",
    source_type="hospital",
    zip_code="22042",
)`,
    curl: `curl https://api.legacygrowthengine.com/v1/contacts \\
  -H "Authorization: Bearer $LGE_API_KEY" \\
  -d '{"org_name":"Inova Fairfax Hospital","source_type":"hospital","zip_code":"22042"}'`,
  },
  "contacts-update": {
    javascript: `await lge.contacts.update('con_3b1f9e2d', { notes: 'Follow up Q2.' });`,
    python: `lge.contacts.update("con_3b1f9e2d", notes="Follow up Q2.")`,
    curl: `curl -X PATCH https://api.legacygrowthengine.com/v1/contacts/con_3b1f9e2d \\
  -H "Authorization: Bearer $LGE_API_KEY" \\
  -d '{"notes":"Follow up Q2."}'`,
  },
  "contacts-delete": {
    javascript: `await lge.contacts.delete('con_3b1f9e2d');`,
    python: `lge.contacts.delete("con_3b1f9e2d")`,
    curl: `curl -X DELETE https://api.legacygrowthengine.com/v1/contacts/con_3b1f9e2d \\
  -H "Authorization: Bearer $LGE_API_KEY"`,
  },
  "orgs-retrieve": {
    javascript: `const org = await lge.organization.retrieve();`,
    python: `org = lge.organization.retrieve()`,
    curl: `curl https://api.legacygrowthengine.com/v1/organization \\
  -H "Authorization: Bearer $LGE_API_KEY"`,
  },
  "orgs-update": {
    javascript: `await lge.organization.update({ booking_link: 'https://cal.com/agency' });`,
    python: `lge.organization.update(booking_link="https://cal.com/agency")`,
    curl: `curl -X PATCH https://api.legacygrowthengine.com/v1/organization \\
  -H "Authorization: Bearer $LGE_API_KEY" \\
  -d '{"booking_link":"https://cal.com/agency"}'`,
  },
  "team-list": {
    javascript: `const members = await lge.team.list();`,
    python: `members = lge.team.list()`,
    curl: `curl https://api.legacygrowthengine.com/v1/team \\
  -H "Authorization: Bearer $LGE_API_KEY"`,
  },
  "team-invite": {
    javascript: `await lge.team.invite({
  email: 'jordan@homecare.example',
  role: 'manager',
});`,
    python: `lge.team.invite(email="jordan@homecare.example", role="manager")`,
    curl: `curl -X POST https://api.legacygrowthengine.com/v1/team/invites \\
  -H "Authorization: Bearer $LGE_API_KEY" \\
  -d '{"email":"jordan@homecare.example","role":"manager"}'`,
  },
  "team-retrieve": {
    javascript: `const member = await lge.team.retrieve('usr_7c2e4d1a');`,
    python: `member = lge.team.retrieve("usr_7c2e4d1a")`,
    curl: `curl https://api.legacygrowthengine.com/v1/team/usr_7c2e4d1a \\
  -H "Authorization: Bearer $LGE_API_KEY"`,
  },
  "team-update": {
    javascript: `await lge.team.update('usr_7c2e4d1a', { role: 'staff' });`,
    python: `lge.team.update("usr_7c2e4d1a", role="staff")`,
    curl: `curl -X PATCH https://api.legacygrowthengine.com/v1/team/usr_7c2e4d1a \\
  -H "Authorization: Bearer $LGE_API_KEY" \\
  -d '{"role":"staff"}'`,
  },
  "team-remove": {
    javascript: `await lge.team.remove('usr_7c2e4d1a');`,
    python: `lge.team.remove("usr_7c2e4d1a")`,
    curl: `curl -X DELETE https://api.legacygrowthengine.com/v1/team/usr_7c2e4d1a \\
  -H "Authorization: Bearer $LGE_API_KEY"`,
  },
  "analytics-overview": {
    javascript: `const metrics = await lge.analytics.overview({ range: '30d' });`,
    python: `metrics = lge.analytics.overview(range="30d")`,
    curl: `curl "https://api.legacygrowthengine.com/v1/analytics/overview?range=30d" \\
  -H "Authorization: Bearer $LGE_API_KEY"`,
  },
  "analytics-leads": {
    javascript: `const data = await lge.analytics.leads({ range: '30d', group_by: 'source' });`,
    python: `data = lge.analytics.leads(range="30d", group_by="source")`,
    curl: `curl "https://api.legacygrowthengine.com/v1/analytics/leads?range=30d" \\
  -H "Authorization: Bearer $LGE_API_KEY"`,
  },
  "analytics-conversion": {
    javascript: `const funnel = await lge.analytics.conversion({ range: '90d' });`,
    python: `funnel = lge.analytics.conversion(range="90d")`,
    curl: `curl "https://api.legacygrowthengine.com/v1/analytics/conversion?range=90d" \\
  -H "Authorization: Bearer $LGE_API_KEY"`,
  },
  "analytics-team": {
    javascript: `const activity = await lge.analytics.team({ range: '7d' });`,
    python: `activity = lge.analytics.team(range="7d")`,
    curl: `curl "https://api.legacygrowthengine.com/v1/analytics/team?range=7d" \\
  -H "Authorization: Bearer $LGE_API_KEY"`,
  },
  "outreach-generate": {
    javascript: `const draft = await lge.outreach.generate({
  contact_id: 'con_3b1f9e2d',
  source_type: 'hospital',
});`,
    python: `draft = lge.outreach.generate(contact_id="con_3b1f9e2d", source_type="hospital")`,
    curl: `curl -X POST https://api.legacygrowthengine.com/v1/outreach/generate \\
  -H "Authorization: Bearer $LGE_API_KEY" \\
  -d '{"contact_id":"con_3b1f9e2d","source_type":"hospital"}'`,
  },
  "outreach-send": {
    javascript: `await lge.outreach.send({
  contact_id: 'con_3b1f9e2d',
  subject: 'Partnering for discharge planning',
  body: 'Hi Sarah, ...',
});`,
    python: `lge.outreach.send(contact_id="con_3b1f9e2d", subject="...", body="...")`,
    curl: `curl -X POST https://api.legacygrowthengine.com/v1/outreach/send \\
  -H "Authorization: Bearer $LGE_API_KEY" \\
  -d '{"contact_id":"con_3b1f9e2d","subject":"...","body":"..."}'`,
  },
  "outreach-schedule": {
    javascript: `await lge.outreach.schedule({
  contact_id: 'con_3b1f9e2d',
  send_at: '2026-04-25T14:00:00Z',
  subject: '...',
  body: '...',
});`,
    python: `lge.outreach.schedule(contact_id="con_3b1f9e2d", send_at="2026-04-25T14:00:00Z")`,
    curl: `curl -X POST https://api.legacygrowthengine.com/v1/outreach/schedule \\
  -H "Authorization: Bearer $LGE_API_KEY" \\
  -d '{"contact_id":"con_3b1f9e2d","send_at":"2026-04-25T14:00:00Z"}'`,
  },
  "webhooks-overview": {
    javascript: `const endpoint = await lge.webhooks.create({
  url: 'https://example.com/lge-webhook',
  events: ['lead.created', 'lead.scored'],
});`,
    python: `endpoint = lge.webhooks.create(
    url="https://example.com/lge-webhook",
    events=["lead.created", "lead.scored"],
)`,
    curl: `curl -X POST https://api.legacygrowthengine.com/v1/webhooks \\
  -H "Authorization: Bearer $LGE_API_KEY" \\
  -d '{"url":"https://example.com/lge-webhook","events":["lead.created"]}'`,
  },
  "webhooks-signatures": {
    javascript: `import crypto from 'crypto';

function verify(payload, signature, secret) {
  const expected = crypto
    .createHmac('sha256', secret)
    .update(payload)
    .digest('hex');
  return crypto.timingSafeEqual(
    Buffer.from(signature),
    Buffer.from(expected),
  );
}`,
    python: `import hmac, hashlib

def verify(payload: bytes, signature: str, secret: str) -> bool:
    expected = hmac.new(secret.encode(), payload, hashlib.sha256).hexdigest()
    return hmac.compare_digest(signature, expected)`,
    curl: `# Verification happens server-side. Compare the
# X-LGE-Signature header against an HMAC-SHA256 of the raw body.`,
  },
};

// Default snippet used when a section has no specific code example.
const DEFAULT_CODE: CodeSnippets = CODE.quickstart;

// =============================================================================
// HIGHLIGHTER (regex-based, intentionally simple)
// =============================================================================

function highlightCode(code: string, lang: Lang): string {
  // Escape HTML first
  const esc = code
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  let html = esc;

  if (lang === "javascript") {
    html = html.replace(/(\/\/[^\n]*)/g, '<span style="color:#6B7C93">$1</span>');
    html = html.replace(/('[^']*'|"[^"]*"|`[^`]*`)/g, '<span style="color:#9DECF9">$1</span>');
    html = html.replace(
      /\b(import|from|const|let|var|function|return|await|async|new|if|else|for|while|class|export|default)\b/g,
      '<span style="color:#F9A8D4">$1</span>'
    );
    html = html.replace(/\b(\d+)\b/g, '<span style="color:#FCD34D">$1</span>');
  } else if (lang === "python") {
    html = html.replace(/(#[^\n]*)/g, '<span style="color:#6B7C93">$1</span>');
    html = html.replace(/('[^']*'|"[^"]*")/g, '<span style="color:#9DECF9">$1</span>');
    html = html.replace(
      /\b(import|from|def|return|class|if|else|elif|for|while|in|as|with|lambda|None|True|False)\b/g,
      '<span style="color:#F9A8D4">$1</span>'
    );
    html = html.replace(/\b(\d+)\b/g, '<span style="color:#FCD34D">$1</span>');
  } else {
    html = html.replace(/(#[^\n]*)/g, '<span style="color:#6B7C93">$1</span>');
    html = html.replace(/('[^']*'|"[^"]*")/g, '<span style="color:#9DECF9">$1</span>');
    html = html.replace(/(\bcurl\b|-[A-Za-z]\b|--[a-z-]+)/g, '<span style="color:#F9A8D4">$1</span>');
  }

  return html;
}

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

function CodeBlock({
  code, lang, dark, label, onCopy, copied,
}: {
  code: string; lang: Lang; dark: boolean; label?: string;
  onCopy: () => void; copied: boolean;
}) {
  return (
    <div className="rounded-lg overflow-hidden my-4" style={{ background: "#0F1E35" }}>
      <div className="flex items-center justify-between px-4 py-2 border-b border-white/10">
        <span className="text-xs font-mono text-gray-400">{label || lang}</span>
        <button
          onClick={onCopy}
          className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white transition-colors"
        >
          {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
        </button>
      </div>
      <pre className="p-4 overflow-x-auto text-sm leading-relaxed">
        <code
          className="font-mono text-gray-100"
          dangerouslySetInnerHTML={{ __html: highlightCode(code, lang) }}
        />
      </pre>
    </div>
  );
}

function EndpointBadge({ method, path }: { method: string; path: string }) {
  const colors: Record<string, string> = {
    GET: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
    POST: "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300",
    PATCH: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
    DELETE: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300",
  };
  return (
    <div className="flex items-center gap-3 my-3 flex-wrap">
      <span className={`px-2.5 py-1 rounded text-xs font-bold font-mono ${colors[method] || colors.GET}`}>
        {method}
      </span>
      <code className="font-mono text-sm text-gray-800 dark:text-gray-200 break-all">{path}</code>
    </div>
  );
}

function ResponseBadge({ code, label }: { code: number; label: string }) {
  const color =
    code < 300 ? "bg-emerald-100 text-emerald-700"
    : code < 500 ? "bg-amber-100 text-amber-700"
    : "bg-red-100 text-red-700";
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-mono font-semibold ${color}`}>
      {code} {label}
    </span>
  );
}

function Callout({ type = "note", children }: { type?: "note" | "warning" | "tip"; children: React.ReactNode }) {
  const config = {
    note: { icon: Info, bg: "bg-blue-50 dark:bg-blue-950/40", border: "border-blue-200 dark:border-blue-900", color: "text-blue-700 dark:text-blue-300" },
    warning: { icon: AlertCircle, bg: "bg-amber-50 dark:bg-amber-950/40", border: "border-amber-200 dark:border-amber-900", color: "text-amber-700 dark:text-amber-300" },
    tip: { icon: Lightbulb, bg: "bg-emerald-50 dark:bg-emerald-950/40", border: "border-emerald-200 dark:border-emerald-900", color: "text-emerald-700 dark:text-emerald-300" },
  }[type];
  const Icon = config.icon;
  return (
    <div className={`flex gap-3 my-4 p-4 rounded-lg border ${config.bg} ${config.border}`}>
      <Icon size={18} className={`shrink-0 mt-0.5 ${config.color}`} />
      <div className={`text-sm leading-relaxed ${config.color}`}>{children}</div>
    </div>
  );
}

type Param = { name: string; type: string; required?: boolean; description: string };

function ParamTable({ title, params }: { title: string; params: Param[] }) {
  return (
    <div className="my-5">
      <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wider">{title}</h4>
      <div className="border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
        {params.map((p, i) => (
          <div
            key={p.name}
            className={`grid grid-cols-12 gap-3 px-4 py-3 text-sm ${i > 0 ? "border-t border-gray-200 dark:border-gray-800" : ""}`}
          >
            <div className="col-span-12 sm:col-span-3">
              <code className="font-mono text-gray-900 dark:text-gray-100 font-semibold">{p.name}</code>
              {p.required && <span className="ml-2 text-xs text-red-600 dark:text-red-400 font-semibold">required</span>}
            </div>
            <div className="col-span-12 sm:col-span-2">
              <span className="text-xs font-mono text-gray-500 dark:text-gray-400">{p.type}</span>
            </div>
            <div className="col-span-12 sm:col-span-7 text-gray-600 dark:text-gray-300 leading-relaxed">
              {p.description}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SectionHeading({ id, children }: { id: string; children: React.ReactNode }) {
  const [copied, setCopied] = useState(false);
  const onCopy = () => {
    const url = `${window.location.origin}/api-docs?section=${id}`;
    navigator.clipboard?.writeText(url).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <h2 id={id} className="group flex items-center gap-2 text-2xl font-bold text-gray-900 dark:text-gray-50 mt-12 mb-4 scroll-mt-20">
      {children}
      <button
        onClick={onCopy}
        className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-opacity"
        aria-label="Copy link"
      >
        {copied ? <Check size={16} /> : <Link2 size={16} />}
      </button>
    </h2>
  );
}

function SubHeading({ id, children }: { id?: string; children: React.ReactNode }) {
  return (
    <h3 id={id} className="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-8 mb-3 scroll-mt-20">
      {children}
    </h3>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return <p className="text-base text-gray-700 dark:text-gray-300 leading-relaxed my-3">{children}</p>;
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function ApiDocs() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeSection, setActiveSection] = useState("introduction");
  const [activeLang, setActiveLang] = useState<Lang>("javascript");
  const [dark, setDark] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(NAV_GROUPS.map((g) => [g.id, true]))
  );
  const [copiedBlock, setCopiedBlock] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Inter font
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap";
    document.head.appendChild(link);
    return () => { document.head.removeChild(link); };
  }, []);

  // Dark mode (system + manual override)
  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    setDark(mq.matches);
    const handler = (e: MediaQueryListEvent) => setDark(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  // Deep link on mount
  useEffect(() => {
    const section = searchParams.get("section");
    if (section) {
      setTimeout(() => {
        const el = document.getElementById(section);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // IntersectionObserver for active section tracking
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveSection(visible[0].target.id);
      },
      { rootMargin: "-80px 0px -60% 0px", threshold: 0 }
    );
    document.querySelectorAll("section[id]").forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  // Cmd/Ctrl+K
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
      if (e.key === "Escape") setSearchOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Navigate to section
  const navigateTo = useCallback((id: string) => {
    setSearchParams({ section: id }, { replace: true });
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setSidebarOpen(false);
    setSearchOpen(false);
  }, [setSearchParams]);

  // Copy code
  const copyCode = useCallback((blockId: string, code: string) => {
    navigator.clipboard?.writeText(code).catch(() => {
      const ta = document.createElement("textarea");
      ta.value = code;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch {}
      document.body.removeChild(ta);
    });
    setCopiedBlock(blockId);
    setTimeout(() => setCopiedBlock(null), 2000);
  }, []);

  // Search results
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return SEARCH_INDEX.slice(0, 8);
    const q = searchQuery.toLowerCase();
    return SEARCH_INDEX.filter((e) => e.keywords.includes(q)).slice(0, 12);
  }, [searchQuery]);

  // Get code for current/specific section
  const codeFor = (id: string): CodeSnippets => CODE[id] || DEFAULT_CODE;

  const themeClass = dark ? "dark" : "";

  return (
    <div className={themeClass} style={{ fontFamily: "Inter, system-ui, sans-serif" }}>
      <div className="min-h-screen bg-white dark:bg-[#0A1628] text-gray-900 dark:text-gray-100 transition-colors">
        {/* TOP BAR */}
        <header className="sticky top-0 z-40 h-14 border-b border-gray-200 dark:border-gray-800 bg-white/90 dark:bg-[#0A1628]/90 backdrop-blur">
          <div className="h-full flex items-center justify-between px-4 lg:px-6">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden text-gray-600 dark:text-gray-400"
                aria-label="Open menu"
              >
                <Menu size={22} />
              </button>
              <Link to="/" className="flex items-center gap-2">
                <span className="font-bold text-base" style={{ color: dark ? "#D4A843" : "#1F3864" }}>
                  LegacyGrowth
                </span>
                <span className="hidden sm:inline text-xs font-mono px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
                  API Docs
                </span>
              </Link>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSearchOpen(true)}
                className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-md border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-600"
              >
                <Search size={14} />
                <span className="hidden sm:inline">Search</span>
                <kbd className="hidden sm:inline text-xs font-mono px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800">⌘K</kbd>
              </button>
              <button
                onClick={() => setDark((d) => !d)}
                className="p-2 rounded-md text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                aria-label="Toggle theme"
              >
                {dark ? <Sun size={16} /> : <Moon size={16} />}
              </button>
              <Link
                to="/login"
                className="hidden sm:inline-block px-3 py-1.5 text-sm font-semibold rounded-md text-white"
                style={{ background: "#D4A843" }}
              >
                Dashboard
              </Link>
            </div>
          </div>
        </header>

        <div className="flex">
          {/* SIDEBAR */}
          <aside
            className={`fixed lg:sticky top-14 left-0 z-30 h-[calc(100vh-3.5rem)] w-[260px] overflow-y-auto bg-white dark:bg-[#0A1628] border-r border-gray-200 dark:border-gray-800 transition-transform ${
              sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
            }`}
          >
            <div className="lg:hidden flex justify-end p-3">
              <button onClick={() => setSidebarOpen(false)} aria-label="Close menu">
                <X size={20} className="text-gray-500" />
              </button>
            </div>
            <nav className="px-3 pt-3 pb-12">
              {NAV_GROUPS.map((group) => {
                const Icon = group.icon;
                const expanded = expandedGroups[group.id];
                return (
                  <div key={group.id} className="mb-1">
                    <button
                      onClick={() =>
                        setExpandedGroups((s) => ({ ...s, [group.id]: !s[group.id] }))
                      }
                      className="w-full flex items-center gap-2 px-2 py-1.5 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                    >
                      <Icon size={13} />
                      <span className="flex-1 text-left">{group.title}</span>
                      {expanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                    </button>
                    {expanded && (
                      <div className="ml-1 mt-0.5 mb-2 border-l border-gray-200 dark:border-gray-800">
                        {group.items.map((item) => {
                          const active = activeSection === item.id;
                          return (
                            <button
                              key={item.id}
                              onClick={() => navigateTo(item.id)}
                              className={`w-full text-left text-sm pl-4 pr-2 py-1.5 -ml-px border-l-2 transition-colors ${
                                active
                                  ? "border-[#D4A843] text-[#D4A843] font-semibold"
                                  : "border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                              }`}
                            >
                              {item.title}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>
          </aside>

          {sidebarOpen && (
            <div
              className="lg:hidden fixed inset-0 top-14 z-20 bg-black/40"
              onClick={() => setSidebarOpen(false)}
            />
          )}

          {/* CONTENT + RIGHT PANEL */}
          <div className="flex-1 min-w-0 flex">
            <main ref={contentRef} className="flex-1 min-w-0 px-4 sm:px-8 py-8 max-w-3xl xl:mx-0 mx-auto">

              {/* GETTING STARTED */}
              <section id="introduction" className="scroll-mt-20">
                <p className="text-xs font-semibold uppercase tracking-wider text-[#D4A843] mb-2">Getting Started</p>
                <SectionHeading id="introduction">Introduction</SectionHeading>
                <P>
                  The LegacyGrowth Engine API is a RESTful interface that lets you programmatically manage leads,
                  referral contacts, team members, and outreach campaigns from your own systems. The API is organized
                  around predictable resource-oriented URLs, accepts JSON request bodies, returns JSON responses, and
                  uses standard HTTP response codes.
                </P>
                <P>
                  This documentation describes <strong>v1</strong> of the API. All endpoints are accessed over HTTPS
                  and require authentication via a Bearer token.
                </P>
                <Callout type="note">
                  <strong>HIPAA-aware:</strong> All API requests are logged and audited. Personal health information
                  is encrypted at rest and in transit. A signed BAA is required before production use.
                </Callout>
                <Callout type="tip">
                  <strong>OpenAPI 3.1 spec:</strong> The full machine-readable specification is available at{" "}
                  <a
                    href="/v1/openapi.json"
                    target="_blank"
                    rel="noreferrer"
                    className="font-mono text-[#1F3864] underline hover:text-[#D4A843] dark:text-[#D4A843]"
                  >
                    /v1/openapi.json
                  </a>
                  . Import it into Postman, Insomnia, or any OpenAPI-compatible client — or open the{" "}
                  <Link to="/api-docs/explorer" className="font-medium text-[#1F3864] underline hover:text-[#D4A843] dark:text-[#D4A843]">
                    interactive API explorer
                  </Link>{" "}
                  to try requests right in your browser.
                </Callout>
              </section>

              <section id="quickstart" className="scroll-mt-20">
                <SectionHeading id="quickstart">Quickstart</SectionHeading>
                <P>
                  Create your first lead in under 60 seconds. Grab an API key from your dashboard, install the SDK
                  for your language, and make a single request.
                </P>
                <SubHeading>1. Install the SDK</SubHeading>
                <CodeBlock
                  code={activeLang === "python" ? "pip install legacygrowth" : activeLang === "curl" ? "# No install required for cURL" : "npm install @legacygrowth/node"}
                  lang={activeLang}
                  dark={dark}
                  label="install"
                  copied={copiedBlock === "qs-install"}
                  onCopy={() => copyCode("qs-install", activeLang === "python" ? "pip install legacygrowth" : "npm install @legacygrowth/node")}
                />
                <SubHeading>2. Create a lead</SubHeading>
                <CodeBlock
                  code={codeFor("quickstart")[activeLang]}
                  lang={activeLang}
                  dark={dark}
                  copied={copiedBlock === "qs-create"}
                  onCopy={() => copyCode("qs-create", codeFor("quickstart")[activeLang])}
                />
              </section>

              <section id="base-url" className="scroll-mt-20">
                <SectionHeading id="base-url">Base URL</SectionHeading>
                <P>All API requests are made against a single base URL:</P>
                <CodeBlock
                  code={BASE_URL}
                  lang="curl"
                  dark={dark}
                  label="base url"
                  copied={copiedBlock === "base-url"}
                  onCopy={() => copyCode("base-url", BASE_URL)}
                />
                <P>The API is served exclusively over HTTPS. HTTP requests will be rejected with a 426 Upgrade Required.</P>
              </section>

              <section id="api-versioning" className="scroll-mt-20">
                <SectionHeading id="api-versioning">API Versioning</SectionHeading>
                <P>
                  The API is versioned in the URL path (<code className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 rounded text-sm font-mono">/v1</code>).
                  Within a major version, additive changes (new fields, new endpoints) may be deployed without notice.
                  Breaking changes will only ship under a new major version.
                </P>
                <P>
                  You can pin a specific dated version by sending the <code className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 rounded text-sm font-mono">LGE-Version</code> header:
                </P>
                <CodeBlock
                  code={`LGE-Version: ${API_VERSION}`}
                  lang="curl"
                  dark={dark}
                  label="header"
                  copied={copiedBlock === "ver-header"}
                  onCopy={() => copyCode("ver-header", `LGE-Version: ${API_VERSION}`)}
                />
              </section>

              {/* AUTHENTICATION */}
              <section id="api-keys" className="scroll-mt-20">
                <p className="text-xs font-semibold uppercase tracking-wider text-[#D4A843] mb-2 mt-16">Authentication</p>
                <SectionHeading id="api-keys">API Keys</SectionHeading>
                <P>
                  API keys are issued from your dashboard under <strong>Settings → API Keys</strong>. Each key is
                  scoped to a single organization and can be assigned read or write privileges.
                </P>
                <Callout type="warning">
                  <strong>Keep your keys secret.</strong> Never commit API keys to source control or expose them in
                  client-side code. Use environment variables on your server.
                </Callout>
              </section>

              <section id="bearer-tokens" className="scroll-mt-20">
                <SectionHeading id="bearer-tokens">Bearer Tokens</SectionHeading>
                <P>Send your API key as a Bearer token in the <code className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 rounded text-sm font-mono">Authorization</code> header:</P>
                <CodeBlock
                  code={`Authorization: Bearer lge_live_8f3a2c1b9e7d4f5a6b8c2d1e3f4a5b6c`}
                  lang="curl"
                  dark={dark}
                  label="header"
                  copied={copiedBlock === "bearer"}
                  onCopy={() => copyCode("bearer", "Authorization: Bearer lge_live_...")}
                />
                <P>
                  Keys prefixed with <code className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 rounded text-sm font-mono">lge_test_</code> operate
                  against the sandbox environment. Keys prefixed with <code className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 rounded text-sm font-mono">lge_live_</code> hit production data.
                </P>
              </section>

              <section id="scopes" className="scroll-mt-20">
                <SectionHeading id="scopes">Scopes</SectionHeading>
                <P>Each API key carries a set of scopes that define what it can access:</P>
                <ParamTable
                  title="Available Scopes"
                  params={[
                    { name: "leads:read", type: "scope", description: "Read access to leads, assignments, and notes." },
                    { name: "leads:write", type: "scope", description: "Create, update, and delete leads." },
                    { name: "contacts:read", type: "scope", description: "Read referral contacts." },
                    { name: "contacts:write", type: "scope", description: "Manage referral contacts." },
                    { name: "analytics:read", type: "scope", description: "Read analytics and reporting data." },
                    { name: "team:write", type: "scope", description: "Invite and manage team members." },
                    { name: "webhooks:write", type: "scope", description: "Create and manage webhook endpoints." },
                  ]}
                />
              </section>

              <section id="key-rotation" className="scroll-mt-20">
                <SectionHeading id="key-rotation">Key Rotation</SectionHeading>
                <P>
                  Rotate API keys regularly — at least every 90 days, and immediately if you suspect a key has been
                  compromised. From the dashboard you can issue a replacement key, then revoke the old one once your
                  systems have switched over.
                </P>
                <Callout type="tip">
                  Our SDKs accept multiple keys at once so you can roll out a new key without downtime.
                </Callout>
              </section>

              {/* LEADS */}
              <section id="leads-list" className="scroll-mt-20">
                <p className="text-xs font-semibold uppercase tracking-wider text-[#D4A843] mb-2 mt-16">Leads</p>
                <SectionHeading id="leads-list">List leads</SectionHeading>
                <EndpointBadge method="GET" path="/v1/leads" />
                <P>Returns a paginated list of leads, ordered by most recently created.</P>
                <ParamTable
                  title="Query Parameters"
                  params={[
                    { name: "limit", type: "integer", description: "Number of records to return. Default 25, max 100." },
                    { name: "starting_after", type: "string", description: "Cursor for pagination — a lead ID." },
                    { name: "status", type: "string", description: "Filter: new, contacted, booked, closed, lost." },
                    { name: "min_score", type: "integer", description: "Return only leads with score ≥ this value." },
                    { name: "assigned_to", type: "string", description: "User ID. Returns leads assigned to that user." },
                  ]}
                />
                <SubHeading>Response</SubHeading>
                <ResponseBadge code={200} label="OK" />
                <CodeBlock
                  code={`{
  "object": "list",
  "data": [
    {
      "id": "lead_8f3a2c1b",
      "object": "lead",
      "name": "Margaret Thompson",
      "score": 92,
      "status": "new",
      "zip_code": "22102",
      "created_at": 1734567890
    }
  ],
  "has_more": true,
  "next_cursor": "lead_8f3a2c1b"
}`}
                  lang="javascript"
                  dark={dark}
                  label="200 OK"
                  copied={copiedBlock === "leads-list-resp"}
                  onCopy={() => copyCode("leads-list-resp", "{...}")}
                />
              </section>

              <section id="leads-create" className="scroll-mt-20">
                <SectionHeading id="leads-create">Create a lead</SectionHeading>
                <EndpointBadge method="POST" path="/v1/leads" />
                <P>Creates a new lead. The lead is automatically scored on creation.</P>
                <ParamTable
                  title="Request Body"
                  params={[
                    { name: "name", type: "string", required: true, description: "Lead's full name." },
                    { name: "zip_code", type: "string", required: true, description: "5-digit ZIP code where service is needed." },
                    { name: "care_needs", type: "string", description: "Free-text description of care or support needs." },
                    { name: "hours_needed", type: "integer", description: "Estimated weekly hours of care." },
                    { name: "payer_source", type: "string", description: "private_pay, ltc_insurance, medicaid_waiver, va, family." },
                    { name: "urgency", type: "integer", description: "1–5 scale. 5 indicates emergency placement." },
                    { name: "transcript", type: "string", description: "Optional transcript from a phone or chat intake." },
                  ]}
                />
                <SubHeading>Response</SubHeading>
                <ResponseBadge code={201} label="Created" />
              </section>

              <section id="leads-retrieve" className="scroll-mt-20">
                <SectionHeading id="leads-retrieve">Retrieve a lead</SectionHeading>
                <EndpointBadge method="GET" path="/v1/leads/{lead_id}" />
                <P>Retrieves the details of an existing lead, including enrichment data and assignment history.</P>
                <ParamTable
                  title="Path Parameters"
                  params={[
                    { name: "lead_id", type: "string", required: true, description: "The ID of the lead to retrieve." },
                  ]}
                />
              </section>

              <section id="leads-update" className="scroll-mt-20">
                <SectionHeading id="leads-update">Update a lead</SectionHeading>
                <EndpointBadge method="PATCH" path="/v1/leads/{lead_id}" />
                <P>Updates the specified lead. Only the fields you pass in are modified.</P>
                <ParamTable
                  title="Request Body"
                  params={[
                    { name: "status", type: "string", description: "new, contacted, booked, closed, lost." },
                    { name: "notes", type: "string", description: "Internal notes appended to the lead's history." },
                    { name: "hours_needed", type: "integer", description: "Updated weekly hours estimate." },
                    { name: "urgency", type: "integer", description: "Updated urgency 1–5." },
                  ]}
                />
              </section>

              <section id="leads-delete" className="scroll-mt-20">
                <SectionHeading id="leads-delete">Delete a lead</SectionHeading>
                <EndpointBadge method="DELETE" path="/v1/leads/{lead_id}" />
                <P>Permanently deletes a lead and all associated assignments, notes, and activity logs.</P>
                <Callout type="warning">
                  This action cannot be undone. For HIPAA compliance, deletion events are recorded in your audit log.
                </Callout>
              </section>

              <section id="leads-assign" className="scroll-mt-20">
                <SectionHeading id="leads-assign">Assign a lead</SectionHeading>
                <EndpointBadge method="POST" path="/v1/leads/{lead_id}/assign" />
                <P>Assigns a lead to a team member. The assignee receives an in-app and email notification.</P>
                <ParamTable
                  title="Request Body"
                  params={[
                    { name: "user_id", type: "string", required: true, description: "ID of the team member to assign to." },
                    { name: "note", type: "string", description: "Optional note to the assignee." },
                  ]}
                />
              </section>

              <section id="leads-score" className="scroll-mt-20">
                <SectionHeading id="leads-score">Re-score a lead</SectionHeading>
                <EndpointBadge method="POST" path="/v1/leads/{lead_id}/rescore" />
                <P>Triggers an immediate re-score of the lead using the latest scoring model and any newly enriched data.</P>
              </section>

              {/* REFERRAL CONTACTS */}
              <section id="contacts-list" className="scroll-mt-20">
                <p className="text-xs font-semibold uppercase tracking-wider text-[#D4A843] mb-2 mt-16">Referral Contacts</p>
                <SectionHeading id="contacts-list">List contacts</SectionHeading>
                <EndpointBadge method="GET" path="/v1/contacts" />
                <P>Lists referral contacts in your outreach pipeline.</P>
                <ParamTable
                  title="Query Parameters"
                  params={[
                    { name: "limit", type: "integer", description: "Number of records to return. Default 25, max 100." },
                    { name: "source_type", type: "string", description: "Filter by source: hospital, snf, social_worker, etc." },
                    { name: "email_status", type: "string", description: "draft, scheduled, sent." },
                  ]}
                />
              </section>

              <section id="contacts-create" className="scroll-mt-20">
                <SectionHeading id="contacts-create">Create a contact</SectionHeading>
                <EndpointBadge method="POST" path="/v1/contacts" />
                <ParamTable
                  title="Request Body"
                  params={[
                    { name: "org_name", type: "string", required: true, description: "Organization name." },
                    { name: "source_type", type: "string", required: true, description: "One of 14 supported source types." },
                    { name: "contact_name", type: "string", description: "Primary point of contact." },
                    { name: "phone", type: "string", description: "Contact phone number." },
                    { name: "zip_code", type: "string", description: "ZIP code." },
                    { name: "address", type: "string", description: "Street address." },
                  ]}
                />
              </section>

              <section id="contacts-update" className="scroll-mt-20">
                <SectionHeading id="contacts-update">Update a contact</SectionHeading>
                <EndpointBadge method="PATCH" path="/v1/contacts/{contact_id}" />
                <P>Updates fields on a referral contact. Pass only the fields you want to change.</P>
              </section>

              <section id="contacts-delete" className="scroll-mt-20">
                <SectionHeading id="contacts-delete">Delete a contact</SectionHeading>
                <EndpointBadge method="DELETE" path="/v1/contacts/{contact_id}" />
                <P>Permanently deletes a referral contact and its outreach history.</P>
              </section>

              {/* ORGANIZATIONS */}
              <section id="orgs-retrieve" className="scroll-mt-20">
                <p className="text-xs font-semibold uppercase tracking-wider text-[#D4A843] mb-2 mt-16">Organizations</p>
                <SectionHeading id="orgs-retrieve">Retrieve organization</SectionHeading>
                <EndpointBadge method="GET" path="/v1/organization" />
                <P>Returns the organization profile for the authenticated API key — no ID required.</P>
              </section>

              <section id="orgs-update" className="scroll-mt-20">
                <SectionHeading id="orgs-update">Update organization</SectionHeading>
                <EndpointBadge method="PATCH" path="/v1/organization" />
                <ParamTable
                  title="Request Body"
                  params={[
                    { name: "name", type: "string", description: "Display name of the organization." },
                    { name: "booking_link", type: "string", description: "URL where prospects book discovery calls." },
                    { name: "service_zips", type: "array", description: "ZIP codes the organization serves." },
                    { name: "specialties", type: "array", description: "Care specialties offered." },
                  ]}
                />
              </section>

              {/* TEAM */}
              <section id="team-list" className="scroll-mt-20">
                <p className="text-xs font-semibold uppercase tracking-wider text-[#D4A843] mb-2 mt-16">Team Members</p>
                <SectionHeading id="team-list">List team members</SectionHeading>
                <EndpointBadge method="GET" path="/v1/team" />
                <P>Returns all team members in your organization, including pending invites.</P>
              </section>

              <section id="team-invite" className="scroll-mt-20">
                <SectionHeading id="team-invite">Invite a member</SectionHeading>
                <EndpointBadge method="POST" path="/v1/team/invites" />
                <ParamTable
                  title="Request Body"
                  params={[
                    { name: "email", type: "string", required: true, description: "Email address of the person to invite." },
                    { name: "role", type: "string", required: true, description: "owner, manager, or staff." },
                    { name: "message", type: "string", description: "Optional personal message included in the invite email." },
                  ]}
                />
              </section>

              <section id="team-retrieve" className="scroll-mt-20">
                <SectionHeading id="team-retrieve">Retrieve a member</SectionHeading>
                <EndpointBadge method="GET" path="/v1/team/{user_id}" />
                <P>Retrieves a single team member's profile, role, and activity summary.</P>
              </section>

              <section id="team-update" className="scroll-mt-20">
                <SectionHeading id="team-update">Update a member</SectionHeading>
                <EndpointBadge method="PATCH" path="/v1/team/{user_id}" />
                <P>Updates a team member's role. Only owners can modify other members.</P>
              </section>

              <section id="team-remove" className="scroll-mt-20">
                <SectionHeading id="team-remove">Remove a member</SectionHeading>
                <EndpointBadge method="DELETE" path="/v1/team/{user_id}" />
                <P>Removes a team member. Their assigned leads must be reassigned first.</P>
              </section>

              {/* ANALYTICS */}
              <section id="analytics-overview" className="scroll-mt-20">
                <p className="text-xs font-semibold uppercase tracking-wider text-[#D4A843] mb-2 mt-16">Analytics</p>
                <SectionHeading id="analytics-overview">Overview metrics</SectionHeading>
                <EndpointBadge method="GET" path="/v1/analytics/overview" />
                <P>Returns headline KPIs: leads captured, hot leads, average score, pipeline value, and conversion rate.</P>
                <ParamTable
                  title="Query Parameters"
                  params={[
                    { name: "range", type: "string", description: "7d, 30d, 90d, ytd. Default 30d." },
                  ]}
                />
              </section>

              <section id="analytics-leads" className="scroll-mt-20">
                <SectionHeading id="analytics-leads">Lead analytics</SectionHeading>
                <EndpointBadge method="GET" path="/v1/analytics/leads" />
                <P>Time-series breakdown of lead volume, score distribution, and source attribution.</P>
              </section>

              <section id="analytics-conversion" className="scroll-mt-20">
                <SectionHeading id="analytics-conversion">Conversion funnel</SectionHeading>
                <EndpointBadge method="GET" path="/v1/analytics/conversion" />
                <P>Returns the full conversion funnel: New → Contacted → Booked → Closed.</P>
              </section>

              <section id="analytics-team" className="scroll-mt-20">
                <SectionHeading id="analytics-team">Team activity</SectionHeading>
                <EndpointBadge method="GET" path="/v1/analytics/team" />
                <P>Per-member activity counts: leads handled, status updates, response time medians.</P>
              </section>

              {/* OUTREACH */}
              <section id="outreach-generate" className="scroll-mt-20">
                <p className="text-xs font-semibold uppercase tracking-wider text-[#D4A843] mb-2 mt-16">Email Outreach</p>
                <SectionHeading id="outreach-generate">Generate email</SectionHeading>
                <EndpointBadge method="POST" path="/v1/outreach/generate" />
                <P>Generates a personalized outreach draft for a referral contact using the AI templates tuned to that source type.</P>
                <ParamTable
                  title="Request Body"
                  params={[
                    { name: "contact_id", type: "string", required: true, description: "The referral contact ID." },
                    { name: "source_type", type: "string", required: true, description: "Source type to template against." },
                    { name: "tone", type: "string", description: "professional, warm, urgent. Default professional." },
                  ]}
                />
              </section>

              <section id="outreach-send" className="scroll-mt-20">
                <SectionHeading id="outreach-send">Send email</SectionHeading>
                <EndpointBadge method="POST" path="/v1/outreach/send" />
                <P>Sends an outreach email immediately. The send is logged to the contact's activity timeline.</P>
              </section>

              <section id="outreach-schedule" className="scroll-mt-20">
                <SectionHeading id="outreach-schedule">Schedule email</SectionHeading>
                <EndpointBadge method="POST" path="/v1/outreach/schedule" />
                <P>Schedules an email to be sent at a future timestamp. Up to 30 days in advance.</P>
              </section>

              {/* WEBHOOKS */}
              <section id="webhooks-overview" className="scroll-mt-20">
                <p className="text-xs font-semibold uppercase tracking-wider text-[#D4A843] mb-2 mt-16">Webhooks</p>
                <SectionHeading id="webhooks-overview">Overview</SectionHeading>
                <P>
                  Webhooks let your servers receive real-time notifications when events happen in your LegacyGrowth
                  Engine account. Register an endpoint URL, subscribe to one or more events, and we'll POST a JSON
                  payload to your endpoint each time an event occurs.
                </P>
              </section>

              <section id="webhooks-events" className="scroll-mt-20">
                <SectionHeading id="webhooks-events">Event types</SectionHeading>
                <ParamTable
                  title="Subscribable Events"
                  params={[
                    { name: "lead.created", type: "event", description: "Fired when a new lead is created." },
                    { name: "lead.scored", type: "event", description: "Fired when a lead is scored or re-scored." },
                    { name: "lead.status_changed", type: "event", description: "Fired when a lead's status changes." },
                    { name: "lead.assigned", type: "event", description: "Fired when a lead is assigned to a team member." },
                    { name: "lead.emergency", type: "event", description: "Fired when a lead scores 100 (emergency)." },
                    { name: "contact.created", type: "event", description: "Fired when a referral contact is added." },
                    { name: "outreach.sent", type: "event", description: "Fired when an outreach email is delivered." },
                    { name: "team.invited", type: "event", description: "Fired when a team invite is sent." },
                  ]}
                />
              </section>

              <section id="webhooks-signatures" className="scroll-mt-20">
                <SectionHeading id="webhooks-signatures">Verifying signatures</SectionHeading>
                <P>
                  Every webhook request includes an <code className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 rounded text-sm font-mono">X-LGE-Signature</code> header
                  containing an HMAC-SHA256 of the raw request body using your endpoint's signing secret. Always verify the signature before processing the payload.
                </P>
              </section>

              <section id="webhooks-retry" className="scroll-mt-20">
                <SectionHeading id="webhooks-retry">Retry policy</SectionHeading>
                <P>
                  If your endpoint returns a non-2xx response, we retry with exponential backoff at 1, 5, 15, 60, and
                  240 minutes. After 5 failed attempts the event is marked as failed and surfaced in your dashboard.
                </P>
              </section>

              <section id="webhooks-testing" className="scroll-mt-20">
                <SectionHeading id="webhooks-testing">Testing webhooks</SectionHeading>
                <P>
                  Use the dashboard's <strong>Send test event</strong> button to deliver a sample payload to your
                  endpoint at any time. You can also replay any historical event from the event log.
                </P>
              </section>

              {/* DATA MODELS */}
              <section id="model-lead" className="scroll-mt-20">
                <p className="text-xs font-semibold uppercase tracking-wider text-[#D4A843] mb-2 mt-16">Data Models</p>
                <SectionHeading id="model-lead">Lead object</SectionHeading>
                <ParamTable
                  title="Attributes"
                  params={[
                    { name: "id", type: "string", description: "Unique identifier, prefixed lead_." },
                    { name: "name", type: "string", description: "Full name of the lead." },
                    { name: "score", type: "integer", description: "0–100 predictive score. ≥85 is hot." },
                    { name: "status", type: "string", description: "new, contacted, booked, closed, lost." },
                    { name: "zip_code", type: "string", description: "5-digit ZIP." },
                    { name: "care_needs", type: "string", description: "Free-text description." },
                    { name: "hours_needed", type: "integer", description: "Weekly hours of care needed." },
                    { name: "payer_source", type: "string", description: "How care will be paid for." },
                    { name: "urgency", type: "integer", description: "1–5, with 5 being emergency." },
                    { name: "assigned_to", type: "string", description: "User ID of the assignee, or null." },
                    { name: "enriched", type: "boolean", description: "Whether AI enrichment has run." },
                    { name: "created_at", type: "timestamp", description: "Unix timestamp." },
                  ]}
                />
              </section>

              <section id="model-contact" className="scroll-mt-20">
                <SectionHeading id="model-contact">Contact object</SectionHeading>
                <ParamTable
                  title="Attributes"
                  params={[
                    { name: "id", type: "string", description: "Unique identifier, prefixed con_." },
                    { name: "org_name", type: "string", description: "Organization name." },
                    { name: "contact_name", type: "string", description: "Primary contact's name." },
                    { name: "source_type", type: "string", description: "One of 14 referral source types." },
                    { name: "phone", type: "string", description: "Phone number." },
                    { name: "address", type: "string", description: "Street address." },
                    { name: "zip_code", type: "string", description: "ZIP code." },
                    { name: "email_status", type: "string", description: "draft, scheduled, sent." },
                    { name: "last_contacted_at", type: "timestamp", description: "Most recent outreach." },
                  ]}
                />
              </section>

              <section id="model-organization" className="scroll-mt-20">
                <SectionHeading id="model-organization">Organization object</SectionHeading>
                <ParamTable
                  title="Attributes"
                  params={[
                    { name: "id", type: "string", description: "Unique identifier, prefixed org_." },
                    { name: "name", type: "string", description: "Organization display name." },
                    { name: "segment", type: "string", description: "homecare or grouphome." },
                    { name: "plan", type: "string", description: "starter, growth, agency_pro." },
                    { name: "service_zips", type: "array", description: "ZIPs the org serves." },
                    { name: "specialties", type: "array", description: "Care specialties." },
                    { name: "booking_link", type: "string", description: "Discovery-call booking URL." },
                  ]}
                />
              </section>

              <section id="model-team-member" className="scroll-mt-20">
                <SectionHeading id="model-team-member">Team Member object</SectionHeading>
                <ParamTable
                  title="Attributes"
                  params={[
                    { name: "id", type: "string", description: "Unique identifier, prefixed usr_." },
                    { name: "email", type: "string", description: "Email address." },
                    { name: "name", type: "string", description: "Display name." },
                    { name: "role", type: "string", description: "owner, manager, or staff." },
                    { name: "last_active_at", type: "timestamp", description: "Most recent activity timestamp." },
                  ]}
                />
              </section>

              <section id="model-event" className="scroll-mt-20">
                <SectionHeading id="model-event">Event object</SectionHeading>
                <ParamTable
                  title="Attributes"
                  params={[
                    { name: "id", type: "string", description: "Unique event ID, prefixed evt_." },
                    { name: "type", type: "string", description: "Event type, e.g. lead.created." },
                    { name: "data", type: "object", description: "The resource that triggered the event." },
                    { name: "created_at", type: "timestamp", description: "Unix timestamp." },
                  ]}
                />
              </section>

              <section id="model-error" className="scroll-mt-20">
                <SectionHeading id="model-error">Error object</SectionHeading>
                <ParamTable
                  title="Attributes"
                  params={[
                    { name: "type", type: "string", description: "Error category, e.g. invalid_request_error." },
                    { name: "code", type: "string", description: "Specific error code." },
                    { name: "message", type: "string", description: "Human-readable description." },
                    { name: "param", type: "string", description: "The parameter the error relates to, if any." },
                  ]}
                />
              </section>

              {/* ERRORS */}
              <section id="errors-codes" className="scroll-mt-20">
                <p className="text-xs font-semibold uppercase tracking-wider text-[#D4A843] mb-2 mt-16">Errors</p>
                <SectionHeading id="errors-codes">Error codes</SectionHeading>
                <P>The API uses standard HTTP status codes to indicate success or failure.</P>
                <div className="my-4 space-y-2">
                  <div className="flex items-center gap-3"><ResponseBadge code={200} label="OK" /><span className="text-sm text-gray-600 dark:text-gray-400">Request succeeded.</span></div>
                  <div className="flex items-center gap-3"><ResponseBadge code={201} label="Created" /><span className="text-sm text-gray-600 dark:text-gray-400">Resource created.</span></div>
                  <div className="flex items-center gap-3"><ResponseBadge code={400} label="Bad Request" /><span className="text-sm text-gray-600 dark:text-gray-400">Invalid parameters.</span></div>
                  <div className="flex items-center gap-3"><ResponseBadge code={401} label="Unauthorized" /><span className="text-sm text-gray-600 dark:text-gray-400">Missing or invalid API key.</span></div>
                  <div className="flex items-center gap-3"><ResponseBadge code={403} label="Forbidden" /><span className="text-sm text-gray-600 dark:text-gray-400">Key lacks the required scope.</span></div>
                  <div className="flex items-center gap-3"><ResponseBadge code={404} label="Not Found" /><span className="text-sm text-gray-600 dark:text-gray-400">Resource doesn't exist.</span></div>
                  <div className="flex items-center gap-3"><ResponseBadge code={429} label="Too Many Requests" /><span className="text-sm text-gray-600 dark:text-gray-400">Rate limit exceeded.</span></div>
                  <div className="flex items-center gap-3"><ResponseBadge code={500} label="Server Error" /><span className="text-sm text-gray-600 dark:text-gray-400">Something went wrong on our side.</span></div>
                </div>
              </section>

              <section id="errors-handling" className="scroll-mt-20">
                <SectionHeading id="errors-handling">Handling errors</SectionHeading>
                <P>All error responses share a consistent shape:</P>
                <CodeBlock
                  code={`{
  "error": {
    "type": "invalid_request_error",
    "code": "missing_required_field",
    "message": "Missing required field: zip_code",
    "param": "zip_code"
  }
}`}
                  lang="javascript"
                  dark={dark}
                  label="error"
                  copied={copiedBlock === "err-shape"}
                  onCopy={() => copyCode("err-shape", "{...}")}
                />
              </section>

              <section id="errors-rate-limit" className="scroll-mt-20">
                <SectionHeading id="errors-rate-limit">Rate limiting</SectionHeading>
                <P>
                  The API allows up to <strong>10,000 requests per hour</strong> per organization. Every response
                  includes the following headers:
                </P>
                <ParamTable
                  title="Rate Limit Headers"
                  params={[
                    { name: "X-RateLimit-Limit", type: "integer", description: "Maximum requests per window." },
                    { name: "X-RateLimit-Remaining", type: "integer", description: "Requests remaining in current window." },
                    { name: "X-RateLimit-Reset", type: "timestamp", description: "Unix timestamp when the window resets." },
                  ]}
                />
                <Callout type="tip">
                  When you receive a 429, wait until the <code className="px-1 py-0.5 bg-gray-100 dark:bg-gray-800 rounded text-xs font-mono">X-RateLimit-Reset</code> timestamp before retrying.
                </Callout>
              </section>

              {/* SDKS */}
              <section id="sdk-node" className="scroll-mt-20">
                <p className="text-xs font-semibold uppercase tracking-wider text-[#D4A843] mb-2 mt-16">SDKs & Tools</p>
                <SectionHeading id="sdk-node">Node.js SDK</SectionHeading>
                <P>The official Node.js SDK supports Node 18+, TypeScript, and ESM/CJS.</P>
                <CodeBlock
                  code="npm install @legacygrowth/node"
                  lang="curl"
                  dark={dark}
                  label="install"
                  copied={copiedBlock === "sdk-node-install"}
                  onCopy={() => copyCode("sdk-node-install", "npm install @legacygrowth/node")}
                />
                <a href="#" className="inline-flex items-center gap-1 text-sm font-semibold text-[#D4A843] hover:underline">
                  GitHub repository <ExternalLink size={12} />
                </a>
              </section>

              <section id="sdk-python" className="scroll-mt-20">
                <SectionHeading id="sdk-python">Python SDK</SectionHeading>
                <P>The official Python SDK supports Python 3.9+.</P>
                <CodeBlock
                  code="pip install legacygrowth"
                  lang="curl"
                  dark={dark}
                  label="install"
                  copied={copiedBlock === "sdk-py-install"}
                  onCopy={() => copyCode("sdk-py-install", "pip install legacygrowth")}
                />
              </section>

              <section id="sdk-cli" className="scroll-mt-20">
                <SectionHeading id="sdk-cli">Command-line tool</SectionHeading>
                <P>The <code className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 rounded text-sm font-mono">lge</code> CLI is great for scripting, debugging webhooks, and bulk imports.</P>
                <CodeBlock
                  code={`brew install legacygrowth/tap/lge
lge login
lge leads list --status new --limit 10`}
                  lang="curl"
                  dark={dark}
                  label="cli"
                  copied={copiedBlock === "cli"}
                  onCopy={() => copyCode("cli", "brew install ...")}
                />
              </section>

              <section id="sdk-postman" className="scroll-mt-20">
                <SectionHeading id="sdk-postman">Postman collection</SectionHeading>
                <P>
                  Import the official Postman collection to explore every endpoint interactively.
                  Includes example requests, sample responses, and a pre-configured environment.
                </P>
                <a href="#" className="inline-flex items-center gap-1 text-sm font-semibold text-[#D4A843] hover:underline">
                  Download collection <ExternalLink size={12} />
                </a>
              </section>

              <section id="sdk-openapi" className="scroll-mt-20">
                <SectionHeading id="sdk-openapi">OpenAPI spec</SectionHeading>
                <P>
                  The full OpenAPI 3.1 specification is available for code generation, API testing, and
                  documentation tooling.
                </P>
                <CodeBlock
                  code={`curl https://api.legacygrowthengine.com/v1/openapi.json -o lge-openapi.json`}
                  lang="curl"
                  dark={dark}
                  label="download"
                  copied={copiedBlock === "openapi"}
                  onCopy={() => copyCode("openapi", "curl ...")}
                />
              </section>

              {/* CHANGELOG */}
              <section id="changelog-v1" className="scroll-mt-20">
                <p className="text-xs font-semibold uppercase tracking-wider text-[#D4A843] mb-2 mt-16">Changelog</p>
                <SectionHeading id="changelog-v1">v1.0.0 — Initial release</SectionHeading>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">January 15, 2026</p>
                <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2">
                  <li>Public release of the LegacyGrowth Engine API</li>
                  <li>CRUD endpoints for leads, contacts, organizations, and team members</li>
                  <li>Analytics endpoints for overview, leads, conversion, and team activity</li>
                  <li>Outreach endpoints for AI email generation, sending, and scheduling</li>
                  <li>Webhook subscriptions with HMAC-SHA256 signature verification</li>
                  <li>Official Node.js and Python SDKs</li>
                  <li>OpenAPI 3.1 specification</li>
                </ul>
              </section>

              <div className="h-32" />
            </main>

            {/* RIGHT CODE PANEL */}
            <aside className="hidden xl:block w-[380px] shrink-0 border-l border-gray-200 dark:border-gray-800">
              <div className="sticky top-14 h-[calc(100vh-3.5rem)] overflow-y-auto p-5">
                {/* Lang switcher */}
                <div className="flex gap-1 p-1 rounded-lg bg-gray-100 dark:bg-gray-800 mb-4">
                  {LANGS.map((l) => (
                    <button
                      key={l.id}
                      onClick={() => setActiveLang(l.id)}
                      className={`flex-1 px-2 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                        activeLang === l.id
                          ? "bg-white dark:bg-[#0F1E35] text-gray-900 dark:text-gray-100 shadow-sm"
                          : "text-gray-500 dark:text-gray-400"
                      }`}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>

                <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
                  Example
                </p>
                <CodeBlock
                  code={codeFor(activeSection)[activeLang]}
                  lang={activeLang}
                  dark={dark}
                  label={activeSection}
                  copied={copiedBlock === `panel-${activeSection}`}
                  onCopy={() => copyCode(`panel-${activeSection}`, codeFor(activeSection)[activeLang])}
                />

                {/* Rate limit */}
                <div className="mt-6 p-4 rounded-lg border border-gray-200 dark:border-gray-800">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Rate Limit</span>
                    <Zap size={12} className="text-[#D4A843]" />
                  </div>
                  <div className="text-sm text-gray-700 dark:text-gray-300">
                    <span className="font-bold text-lg text-gray-900 dark:text-gray-100">{RATE_LIMIT.used.toLocaleString()}</span>
                    <span className="text-gray-500 dark:text-gray-400"> / {RATE_LIMIT.total.toLocaleString()}</span>
                  </div>
                  <div className="mt-2 h-1.5 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${(RATE_LIMIT.used / RATE_LIMIT.total) * 100}%`,
                        background: "#D4A843",
                      }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Resets {RATE_LIMIT.reset}</p>
                </div>

                {/* Help */}
                <div className="mt-4 p-4 rounded-lg border border-gray-200 dark:border-gray-800">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield size={14} className="text-[#1F3864] dark:text-[#D4A843]" />
                    <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Need Help?</span>
                  </div>
                  <a href="mailto:api@legacygrowthengine.com" className="block text-sm text-[#D4A843] hover:underline mb-1">
                    api@legacygrowthengine.com
                  </a>
                  <a href="#" className="flex items-center gap-1.5 text-sm text-[#D4A843] hover:underline">
                    <Github size={12} /> Open an issue
                  </a>
                </div>
              </div>
            </aside>
          </div>
        </div>

        {/* SEARCH OVERLAY */}
        {searchOpen && (
          <div
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start justify-center p-4 pt-[10vh]"
            onClick={() => setSearchOpen(false)}
          >
            <div
              className="w-full max-w-xl bg-white dark:bg-[#0F1E35] rounded-xl shadow-2xl overflow-hidden border border-gray-200 dark:border-gray-700"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-800">
                <Search size={18} className="text-gray-400" />
                <input
                  autoFocus
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search the docs..."
                  className="flex-1 bg-transparent text-base text-gray-900 dark:text-gray-100 placeholder-gray-400 outline-none"
                />
                <kbd className="text-xs font-mono px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-500">ESC</kbd>
              </div>
              <div className="max-h-[400px] overflow-y-auto p-2">
                {searchResults.length === 0 ? (
                  <p className="text-center text-sm text-gray-500 py-6">No results.</p>
                ) : (
                  searchResults.map((r) => (
                    <button
                      key={r.id}
                      onClick={() => navigateTo(r.id)}
                      className="w-full flex items-center justify-between px-3 py-2.5 rounded-md text-left hover:bg-gray-100 dark:hover:bg-gray-800"
                    >
                      <div>
                        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{r.title}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{r.breadcrumb}</div>
                      </div>
                      <ChevronRight size={14} className="text-gray-400" />
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
